export interface SparklineProps {
	/**
	 * Qiymatlar, vaqt tartibida.
	 *
	 * ⚠️ **`null` — MA'LUMOTSIZ nuqta, `0` EMAS.** Chiziq shu yerda
	 * uziladi va X o'qi siqilmaydi (dashboard `Sparkline` qoidasi).
	 * Reja imzosi `number[]` edi — `null` qo'llab-quvvatlash dashboard
	 * moslamasi uchun kengaytirildi.
	 */
	points: (number | null)[];
	/** CSS rang ifodasi — `holatVar()` yoki `var(--chart-*)`. */
	color: string;
	width?: number;
	height?: number;
	/** Chiziq ostini och rang bilan to'ldirish. */
	area?: boolean;
	/**
	 * ⚠️ `min`/`max` berilmasa shkala MA'LUMOTDAN olinadi. Foiz kabi
	 * mutlaq shkala kerak bo'lsa ikkalasini ANIQ bering — avtomatik
	 * shkalada 92↔93 tebranish butun oynani to'ldirib «qulash» bo'lib
	 * ko'rinadi.
	 */
	min?: number;
	max?: number;
	/** Gorizontal uzuq maqsad chizig'i (masalan davomat 95%). */
	goal?: number;
	/** Oxirgi nuqta markerining ichki rangi. */
	markerFill?: string;
	/** ⚠️ MAJBURIY — diagramma ekran o'quvchisiga matn bilan yetkaziladi. */
	ariaLabel: string;
}

interface Nuqta {
	x: number;
	y: number;
}

/** SVG atributlari uchun — suzuvchi nuqta shovqinini kesadi. */
function yumaloq(qiymat: number): number {
	return Math.round(qiymat * 100) / 100;
}

function shkala(points: (number | null)[], min?: number, max?: number): [number, number] {
	const mavjud = points.filter((qiymat): qiymat is number => qiymat !== null);
	const past = min ?? (mavjud.length > 0 ? Math.min(...mavjud) : 0);
	const yuqori = max ?? (mavjud.length > 0 ? Math.max(...mavjud) : 1);

	return [past, yuqori];
}

/** Uzluksiz bo'laklar — `null` da uziladi, X INDEKSDAN hisoblanadi. */
function bolaklar(
	points: (number | null)[],
	width: number,
	yHisobi: (qiymat: number) => number
): Nuqta[][] {
	const oxirgiIndeks = Math.max(1, points.length - 1);
	const natija: Nuqta[][] = [];
	let joriy: Nuqta[] = [];

	points.forEach((qiymat, indeks) => {
		if (qiymat === null) {
			if (joriy.length > 0) {
				natija.push(joriy);
				joriy = [];
			}

			return;
		}

		joriy.push({ x: yumaloq((indeks / oxirgiIndeks) * width), y: yHisobi(qiymat) });
	});

	if (joriy.length > 0) {
		natija.push(joriy);
	}

	return natija;
}

function maydon(nuqtalar: Nuqta[], height: number): string {
	const boshi = nuqtalar[0];
	const oxiri = nuqtalar.at(-1);

	if (!(boshi && oxiri)) {
		return "";
	}

	const ustki = nuqtalar.map((nuqta) => `L${nuqta.x},${nuqta.y}`).join(" ");

	return `M${boshi.x},${height} ${ustki} L${oxiri.x},${height} Z`;
}

function yozuv(nuqtalar: Nuqta[]): string {
	// ⚠️ Yolg'iz nuqta TAKRORLANADI: bitta koordinatali `polyline` SVG'da
	// chizilmaydi, takrorlangani `stroke-linecap="round"` bilan nuqta
	// bo'lib ko'rinadi.
	const royxat = nuqtalar.length === 1 ? [nuqtalar[0], nuqtalar[0]] : nuqtalar;

	return royxat.map((nuqta) => `${nuqta?.x ?? 0},${nuqta?.y ?? 0}`).join(" ");
}

/**
 * Kutubxonasiz sparkline (sof SVG).
 *
 * ⚠️ **Har uzluksiz bo'lak — ALOHIDA `polyline`.** Bitta chiziq
 * ma'lumotsiz nuqta ustidan yolg'on interpolyatsiya o'tkazardi.
 * `path` EMAS — dashboard testlari `polyline` ni pinlagan.
 *
 * ⚠️ Kamida ikki mavjud nuqta bo'lmasa chiziq chizilmaydi.
 */
export function Sparkline({
	points,
	color,
	width = 300,
	height = 52,
	area = false,
	min,
	max,
	goal,
	markerFill = "var(--chart-surface)",
	ariaLabel,
}: SparklineProps) {
	const [past, yuqori] = shkala(points, min, max);
	const oraliq = yuqori - past;
	const yHisobi = (qiymat: number): number => {
		// ⚠️ Teng qiymatlar (oraliq 0) — chiziq o'rtada, NaN EMAS.
		if (oraliq <= 0) {
			return yumaloq(height / 2);
		}

		const cheklangan = Math.min(yuqori, Math.max(past, qiymat));
		return yumaloq(height - ((cheklangan - past) / oraliq) * height);
	};

	const mavjud = points.filter((qiymat) => qiymat !== null).length;
	const chiziqlar = mavjud >= 2 ? bolaklar(points, width, yHisobi) : [];
	const oxirgiNuqta = chiziqlar.at(-1)?.at(-1);

	return (
		<svg
			width={width}
			height={height}
			viewBox={`0 0 ${width} ${height}`}
			role="img"
			aria-label={ariaLabel}
			style={{ display: "block", overflow: "visible", maxWidth: "100%" }}
		>
			{goal === undefined || chiziqlar.length === 0 ? null : (
				<line
					data-spark-goal
					x1={0}
					x2={width}
					y1={yHisobi(goal)}
					y2={yHisobi(goal)}
					stroke="var(--chart-axis)"
					strokeWidth={1}
					strokeDasharray="3 3"
				/>
			)}

			{area
				? chiziqlar.map((chiziq) =>
						chiziq.length >= 2 ? (
							<path
								key={`fon-${chiziq[0]?.x}`}
								data-spark-area
								d={maydon(chiziq, height)}
								fill={color}
								fillOpacity={0.1}
							/>
						) : null
					)
				: null}

			{chiziqlar.map((chiziq) => (
				<polyline
					key={`chiziq-${chiziq[0]?.x}`}
					points={yozuv(chiziq)}
					fill="none"
					stroke={color}
					strokeWidth={2}
					strokeLinejoin="round"
					strokeLinecap="round"
				/>
			))}

			{oxirgiNuqta ? (
				<circle
					cx={oxirgiNuqta.x}
					cy={oxirgiNuqta.y}
					r={4}
					fill={markerFill}
					stroke={color}
					strokeWidth={2}
				/>
			) : null}
		</svg>
	);
}
